import React, { useEffect, useState } from 'react';
import { supabase, setJournalUser } from './lib/supabase.js';
import { getPendingCount } from './lib/offline.js';
import PinGuardModal from './components/PinGuardModal.jsx';
import PauseButton from './components/PauseButton.jsx';
import { Boundary, installerGardeFouVersion } from './components/Boundary.jsx';
import Login from './pages/Login.jsx';
import Home from './pages/Home.jsx';
import ReclamationForm from './pages/ReclamationForm.jsx';
import DerogationForm from './pages/DerogationForm.jsx';
import './app.css';

const AUTH_URL = '/.netlify/functions/auth';

// Bandeau global : affiche toute écriture en base qui a échoué (via
// l'intercepteur de lib/supabase.js). Disparaît seul après 12 s.
function DbErrorBanner() {
  const [errs, setErrs] = useState([]);
  useEffect(() => {
    const onErr = (e) => {
      const d = e.detail || {};
      setErrs(prev => [...prev.slice(-2), d]);
      setTimeout(() => setErrs(prev => prev.filter(x => x !== d)), 12000);
    };
    window.addEventListener('elixir:db-error', onErr);
    return () => window.removeEventListener('elixir:db-error', onErr);
  }, []);
  if (!errs.length) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999, padding: '10px 14px', background: '#C0392B', color: 'white', fontSize: 13, fontWeight: 600, boxShadow: '0 2px 10px rgba(0,0,0,.25)', fontFamily: 'inherit' }}>
      {errs.map((d, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>⚠️</span>
          <span style={{ flex: 1 }}>Sauvegarde échouée ({d.table}) : {d.message}. Vérifie que l’action a bien été prise en compte, sinon réessaie ou préviens Zineb.</span>
          <button onClick={() => setErrs(prev => prev.filter(x => x !== d))} style={{ background: 'rgba(255,255,255,.2)', border: 'none', color: 'white', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
        </div>
      ))}
    </div>
  );
}

// Déconnexion automatique après 30 min sans interaction (appareils partagés :
// une action ne doit jamais être enregistrée au nom de quelqu'un d'absent).
const INACTIVITY_MS = 30 * 60 * 1000;
function InactivityLogout({ onLogout }) {
  useEffect(() => {
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const evts = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    evts.forEach(e => window.addEventListener(e, bump, { passive: true }));
    const timer = setInterval(() => {
      if (Date.now() - last > INACTIVITY_MS) {
        clearInterval(timer);
        alert('Session fermée après 30 minutes d\u2019inactivité. Reconnecte-toi avec ton code.');
        onLogout();
      }
    }, 30 * 1000);
    return () => { clearInterval(timer); evts.forEach(e => window.removeEventListener(e, bump)); };
  }, [onLogout]);
  return null;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPublicForm = window.location.pathname.startsWith('/reclamation');
  const isDerogationForm = window.location.pathname.startsWith('/derogation');

  useEffect(() => {
    checkSession();
    // Un fichier de module introuvable (nouvelle version déployée pendant que
    // l'app est ouverte) ne doit plus tuer l'app : on recharge tout seul.
    installerGardeFouVersion();
  }, []);

  async function checkSession() {
    try {
      const res = await fetch(AUTH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'verify', params: {} }),
      });
      const json = await res.json();
      if (json.result?.valid && json.result?.user) {
        try { if (json.result.supaJwt) localStorage.setItem('elixir_supa_jwt', json.result.supaJwt); } catch {}
        setUser(json.result.user);
        setJournalUser(json.result.user);   // le journal doit savoir qui agit
        setLoading(false);
        return;
      }
    } catch (e) {}
    setLoading(false);
  }

  function handleLogin(u) {
    setUser(u);
    setJournalUser(u);
  }

  async function handleLogout() {
    const pending = getPendingCount();
    if (pending > 0) {
      if (!confirm(pending + ' action(s) non synchronisée(s). Continuer ?')) return;
    }
    try {
      await fetch(AUTH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'logout', params: {} }),
      });
    } catch (e) {}
    setUser(null);
    setJournalUser(null);
    localStorage.removeItem('elixir_session');
    try { localStorage.removeItem('elixir_supa_jwt'); } catch {}
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">ELIXIR PREP</div>
        <div className="loading-sub">Chargement…</div>
      </div>
    );
  }

  if (isPublicForm) return <ReclamationForm />;
  if (isDerogationForm) return <DerogationForm />;

  if (!user) {
    return <><DbErrorBanner /><Boundary><Login onLogin={handleLogin} /></Boundary></>;
  }

  // Dernier filet : même une erreur hors module affiche un message et un
  // bouton Recharger, jamais une page blanche.
  return <><DbErrorBanner /><PinGuardModal user={user} /><InactivityLogout onLogout={handleLogout} /><Boundary cle={user?.id}><Home user={user} onLogout={handleLogout} /></Boundary><PauseButton key={user.id} user={user} onLogout={handleLogout} /></>;
}
